import React, { createContext, useContext } from "react";
import { Colors, DEFAULT_B2B_THEME } from "./Colors";

// Types for PDF branding colors
export interface PDFBrandingColors {
	primary: string;
	secondary: string;
	primarylight: string;
	secondarylight: string;
}

// Full branding configuration
export interface PDFBranding {
	colors: PDFBrandingColors;
	logo: string | null;
	squareLogo: string | null;
	companyName: string;
	isCustomBranded: boolean;
}

// Convert hex color to lighter variant (increase lightness)
export const getLightColor = (hex: string): string => {
	// Remove # if present
	const cleanHex = hex.replace("#", "");

	// Parse RGB values
	const r = Number.parseInt(cleanHex.substring(0, 2), 16);
	const g = Number.parseInt(cleanHex.substring(2, 4), 16);
	const b = Number.parseInt(cleanHex.substring(4, 6), 16);

	// Blend with white to create a light variant (90% white, 10% color)
	const lightR = Math.round(r * 0.1 + 255 * 0.9);
	const lightG = Math.round(g * 0.1 + 255 * 0.9);
	const lightB = Math.round(b * 0.1 + 255 * 0.9);

	// Convert back to hex
	const toHex = (n: number) => n.toString(16).padStart(2, "0");
	return `#${toHex(lightR)}${toHex(lightG)}${toHex(lightB)}`;
};

/**
 * Stippl's own branding — the default for a consumer (B2C) export, i.e. any trip that doesn't
 * belong to an organisation.
 *
 * This used to point at DEFAULT_B2B_THEME, which meant every B2C export came out in the B2B
 * house teal (#1b3d49 / #00d4aa) instead of Stippl green. Organisations that need a neutral
 * fallback should use DEFAULT_B2B_BRANDING below, not this.
 */
export const DEFAULT_BRANDING: PDFBranding = {
	colors: {
		primary: Colors.main1,
		secondary: Colors.main2,
		primarylight: Colors.main1light,
		secondarylight: Colors.main2light,
	},
	logo: null,
	squareLogo: null,
	companyName: "Stippl",
	isCustomBranded: false,
};

/**
 * Fallback for an ORGANISATION trip whose organisation has no theme of its own: the B2B house
 * colors. Never the right choice for a trip with no organisation.
 */
export const DEFAULT_B2B_BRANDING: PDFBranding = {
	colors: {
		primary: DEFAULT_B2B_THEME.BRAND_PRIMARY,
		secondary: DEFAULT_B2B_THEME.BRAND_SECONDARY,
		primarylight: getLightColor(DEFAULT_B2B_THEME.BRAND_PRIMARY),
		secondarylight: getLightColor(DEFAULT_B2B_THEME.BRAND_SECONDARY),
	},
	logo: null,
	squareLogo: null,
	companyName: "Stippl",
	isCustomBranded: false,
};

// Generate full branding colors from organisation theme. Only ever called for a trip that has an
// organisation, so a theme missing a field falls back to the B2B house colors, not to Stippl's.
export const generateBrandingColors = (theme: { main1?: string; main2?: string; brandPrimary?: string; brandSecondary?: string }): PDFBrandingColors => {
	const primary = theme.brandPrimary || theme.main1 || DEFAULT_B2B_BRANDING.colors.primary;
	const secondary = theme.brandSecondary || theme.main2 || DEFAULT_B2B_BRANDING.colors.secondary;

	return {
		primary,
		secondary,
		primarylight: getLightColor(primary),
		secondarylight: getLightColor(secondary),
	};
};

// React Context for PDF branding
const PDFBrandingContext = createContext<PDFBranding>(DEFAULT_BRANDING);

// Provider component
export const PDFBrandingProvider: React.FC<{
	branding: PDFBranding;
	children: React.ReactNode;
}> = ({ branding, children }) => {
	return <PDFBrandingContext.Provider value={branding}>{children}</PDFBrandingContext.Provider>;
};

// Hook to access branding in PDF components
export const usePDFBranding = (): PDFBranding => {
	return useContext(PDFBrandingContext);
};

// Helper function to get item color from branding
export const getItemColorFromBranding = (type: string, colors: PDFBrandingColors): string => {
	// All tags and callout blocks now use secondary color
	return type ? colors.secondary : Colors.black;
};

// Helper function to get light color variant from branding
export const getItemLightColorFromBranding = (color: string, colors: PDFBrandingColors): string => {
	if (color === colors.primary) return colors.primarylight;
	if (color === colors.secondary) return colors.secondarylight;
	return Colors.bgDark;
};
